import { useReducedMotion } from 'framer-motion';
import {
  forwardRef,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { _useFocusContext } from '@dynatrace/strato-components/core';
import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
  type DOMProps,
} from '@dynatrace/strato-components/core';

import { TooltipElement } from './TooltipElement.js';
import { usePoliteContainer } from '../hooks/usePoliteContainer.js';
import { useTooltipState } from '../hooks/useTooltipState.js';

// #region Typings
/**
 * @public
 */
export interface TooltipBaseProps
  extends DOMProps,
    StylingProps,
    DataTestId,
    MaskingProps {
  /** Single child of the tooltip considered to trigger the tooltip element. */
  children: ReactElement;

  /**
   * Defines if the tooltip is disabled or not.
   * @defaultValue false
   */
  disabled?: boolean;

  /**
   * Placement of the tooltip relative to its trigger element.
   * @defaultValue 'top'
   */
  placement?:
    | 'top'
    | 'right'
    | 'bottom'
    | 'left'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end'
    | 'right-start'
    | 'right-end'
    | 'left-start'
    | 'left-end';

  /**
   * Defines if the tooltip should be open initially when used uncontrolled.
   * @defaultValue false
   */
  defaultOpen?: boolean;

  /**
   * Defines if the tooltip is open / closed in a controlled component. In
   * this case, you need to react to `onOpenChange` yourself in order
   * to open and close the tooltip.
   */
  open?: boolean;

  /**
   * Defines the type of delay which is used for the tooltip.
   * @defaultValue 'default'
   */
  delay?: 'default' | 'none';

  /** Callback fired when the tooltip opens. */
  onOpenChange?: (isOpen: boolean) => void;

  /** Array of placements to be used as fallback if the tooltip doesn't fit at the specified placement. */
  fallbackPlacements?: Array<
    | 'top'
    | 'right'
    | 'bottom'
    | 'left'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end'
    | 'right-start'
    | 'right-end'
    | 'left-start'
    | 'left-end'
  >;
}

/**
 * @public
 */
export interface TooltipProps extends TooltipBaseProps {
  /** Text displayed in the tooltip component. */
  text: ReactNode;
}
// #endregion Typings

/**
 * Use the `Tooltip` component to show additional text on hover and click on its trigger element.
 * The trigger element can only be an interactive element, like a button or input.
 * @public
 */
export const Tooltip = /* @__PURE__ */ forwardRef<HTMLDivElement, TooltipProps>(
  (props: TooltipProps, forwardedRef) => {
    const {
      children,
      text,
      placement = 'top',
      open,
      defaultOpen,
      onOpenChange,
      delay = 'default',
      disabled,
      id: propId,
      fallbackPlacements,
      style: consumerStyle,
      className: consumerClassName,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    } = props;

    const generatedId = useId();
    const id = propId ?? generatedId;
    const shouldReduceMotion = useReducedMotion();

    const {
      isOpen,
      open: openTooltip,
      close: closeTooltip,
    } = useTooltipState({
      open,
      defaultOpen,
      onOpenChange,
      delay: shouldReduceMotion || delay === 'none' ? 0 : undefined,
    });

    const tooltipWrapperRef = useRef<HTMLSpanElement | null>(null);
    const [tooltipReferenceElement, setTooltipReferenceElement] = useState<
      Element | undefined
    >(undefined);

    /**
     * The tooltip/popper needs a reference element for positioning. As display contents on the span wrapper is necessary
     * to not interfere with flow content of any layout parent, this will invalidate any getBoundingClientRect() results on
     * the wrapping span. That is why we pass the first child element instead. Since the first child element could change
     * dynamically, we need to watch for changes in the DOM, to ensure the tooltip always has a reference element.
     */
    const mutationObserverRef = useRef(
      new MutationObserver((entries) => {
        for (const entry of entries) {
          const firstNode = entry.addedNodes.item(0);
          if (
            entry.type === 'childList' &&
            firstNode !== null &&
            firstNode instanceof Element &&
            firstNode !== tooltipReferenceElement
          ) {
            setTooltipReferenceElement(firstNode);
          }
        }
      }),
    );

    useEffect(
      () => {
        if (tooltipWrapperRef.current) {
          setTooltipReferenceElement(tooltipWrapperRef.current?.children?.[0]);
          mutationObserverRef.current.observe(tooltipWrapperRef.current, {
            childList: true,
            subtree: false, // only observe changes in the direct children of the wrapper, not deeper in the DOM
          });
        }

        return () => closeTooltip();
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    // If disabled switches to true at runtime and a tooltip is shown, it should
    // close immediately
    useEffect(() => {
      if (disabled) {
        closeTooltip();
      }
    }, [closeTooltip, disabled]);

    const isTooltipHovered = useRef(false);

    const handleKeyDown = useCallback(
      (e: KeyboardEvent) => e.code === 'Escape' && closeTooltip(),
      [closeTooltip],
    );

    useEffect(() => {
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [handleKeyDown]);

    // Create a singleton container with live region attribute to read the contents
    // of the tooltip when it pops up
    const PoliteContainer = usePoliteContainer();

    const { modality } = _useFocusContext();

    return (
      <>
        <span
          style={{
            // Safari has currently an outstanding issue that results in content
            // being inaccessible, when rendered within a display: contents.
            display: 'contents',
          }}
          ref={tooltipWrapperRef}
          onMouseEnter={() => !disabled && openTooltip()}
          onMouseLeave={() =>
            // we need to wrap this in a requestAnimationFrame to be able to first set the isTooltipHovered state correctly in case the TooltipElement (including the gap) is hovered
            // if we don't, the tooltip is closed before the onMouseOver event of the TooltipElement is triggered (to set the right state), as the onMouseLeave of the tooltip trigger would be queued up afterwards
            requestAnimationFrame(() => {
              if (!disabled && !isTooltipHovered.current) {
                closeTooltip();
              }
            })
          }
          onFocus={() =>
            !disabled &&
            modality !== 'pointer' &&
            modality !== 'touch' &&
            openTooltip(true)
          }
          onBlur={
            () => !disabled && !isTooltipHovered.current && closeTooltip() // prevent closing when clicking on the gap
          }
          {...remainingProps}
        >
          {children}
        </span>
        <PoliteContainer>
          {isOpen && (
            <TooltipElement
              ref={forwardedRef}
              triggerElement={tooltipReferenceElement}
              placement={placement}
              fallbackPlacements={fallbackPlacements}
              id={id}
              data-testid={dataTestId}
              data-dtrum-mask={dataDtrumMask}
              data-dtrum-allow={dataDtrumAllow}
              className={consumerClassName}
              style={consumerStyle}
              onMouseLeave={() => !disabled && closeTooltip()}
              onTooltipHovered={(isHovered: boolean) =>
                (isTooltipHovered.current = isHovered)
              }
            >
              {text}
            </TooltipElement>
          )}
        </PoliteContainer>
      </>
    );
  },
);
(Tooltip as typeof Tooltip & { displayName: string }).displayName = 'Tooltip';
